import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import {
    FaCalendarAlt,
    FaCertificate,
    FaHistory,
    FaRoad,
    FaTruck,
    FaEye,
} from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { PiTruckTrailerDuotone } from "react-icons/pi";
import { driverRoadTestQueryOptions } from "src/api/mvte/driver-road-test-query-options";
import { SavedExam } from "../types/exam";
import { SelectedDriver } from "../types/selected-driver";
import { TenstreetApplicant } from "src/types/tenstreet-applicant";
import { getFullName } from "../utils/get-full-name";
import clsx from "clsx";

type ExamHistoryProps = {
    licenseNumber: string;
    driver: TenstreetApplicant;
    onStartExam: (driver: SelectedDriver, exam: SavedExam) => void;
};

export function ExamHistoryButton({
    licenseNumber,
    driver,
    onStartExam,
}: ExamHistoryProps) {
    const dialogRef = useRef<HTMLDialogElement>(null);

    const driverExamsQuery = useQuery({
        ...driverRoadTestQueryOptions.driverExamsQueryOptions({
            params: { licenseNumber },
        }),
        enabled: false, // We'll manually enable it when modal opens
    });

    const completedExams = driverExamsQuery.data?.filter(
        (exam) => exam.examStatus === "COMPLETED",
    );

    function handleOpenModal() {
        if (!driverExamsQuery.isLoading && !driverExamsQuery.data) {
            driverExamsQuery.refetch();
        }
        dialogRef.current?.showModal();
    }

    function handleCloseModal() {
        dialogRef.current?.close();
    }

    function handleViewExam(exam: SavedExam) {
        // Create a temporary SelectedDriver with the exam type from the exam
        const tempDriver: SelectedDriver = {
            driver: driver,
            examType: {
                examTypeID: 0, // This won't be used for viewing
                examTypeName: exam.examType,
            },
        };
        onStartExam(tempDriver, exam);
    }

    return (
        <>
            <button
                className="btn btn-ghost btn-sm h-8 min-h-0 px-2"
                onClick={handleOpenModal}
            >
                <FaHistory className="w-3.5 h-3.5" />
            </button>

            <dialog ref={dialogRef} className="modal">
                <div className="modal-box max-w-3xl max-h-[90vh] p-0 flex flex-col overflow-hidden">
                    <div className="sticky top-0 bg-base-100 z-10 border-b">
                        <div className="px-4 py-3 flex justify-between items-center">
                            <h3 className="font-bold text-base flex items-center">
                                <FaHistory className="mr-2 text-primary" />
                                Exam History
                                <div className="badge badge-ghost ml-2 text-xs">
                                    {getFullName(
                                        driver.firstName,
                                        driver.middleName,
                                        driver.lastName,
                                    )}
                                </div>
                            </h3>
                            <button
                                onClick={handleCloseModal}
                                className="btn btn-sm btn-circle btn-ghost"
                            >
                                <MdClose className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-3">
                        {driverExamsQuery.isLoading && (
                            <div className="flex flex-col items-center justify-center py-6">
                                <div className="loading loading-spinner text-primary loading-md"></div>
                                <span className="mt-2 text-xs text-base-content/70">
                                    Loading exam history...
                                </span>
                            </div>
                        )}

                        {driverExamsQuery.isError && (
                            <div className="alert alert-error shadow-sm py-2">
                                <div className="flex items-center">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-5 w-5 flex-shrink-0 mr-2"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                    <div>
                                        <h3 className="font-bold text-sm">
                                            Failed to load exam history
                                        </h3>
                                        <div className="text-xs">
                                            Please try again later
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {completedExams && completedExams.length === 0 && (
                            <div className="alert alert-info shadow-sm py-2">
                                <div className="flex items-center">
                                    <FaHistory className="h-5 w-5 flex-shrink-0 mr-2" />
                                    <div>
                                        <h3 className="font-bold text-sm">
                                            No exam history found
                                        </h3>
                                        <div className="text-xs">
                                            This driver has no recorded exams
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {completedExams && completedExams.length > 0 && (
                            <div className="space-y-2">
                                {completedExams.map((exam) => (
                                    <div
                                        key={exam.examId}
                                        className="card bg-base-100 border border-base-200 shadow-sm hover:shadow-md overflow-hidden"
                                    >
                                        <div className="card-body p-3">
                                            <div className="flex justify-between items-center mb-2">
                                                <h2 className="card-title text-sm font-medium">
                                                    {exam.examType}
                                                </h2>
                                                <div className="flex items-center gap-2">
                                                    {exam.examResult && (
                                                        <div
                                                            className={clsx(
                                                                "badge badge-sm",
                                                                {
                                                                    "bg-success text-success-content":
                                                                        exam.examResult ===
                                                                        "Qualified",
                                                                    "bg-warning text-warning-content":
                                                                        exam.examResult ===
                                                                        "Qualified with additional training",
                                                                    "bg-error text-error-content":
                                                                        exam.examResult ===
                                                                        "Not Qualified",
                                                                },
                                                            )}
                                                        >
                                                            {exam.examResult}
                                                        </div>
                                                    )}

                                                    <button
                                                        className="btn btn-ghost btn-sm h-8 min-h-0 px-2"
                                                        onClick={() =>
                                                            handleViewExam(exam)
                                                        }
                                                    >
                                                        <FaEye className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-xs">
                                                {exam.examCompletedDateUTC && (
                                                    <div className="flex items-center">
                                                        <div className="bg-base-200 p-1 rounded-full mr-1.5">
                                                            <FaCalendarAlt className="text-primary w-3 h-3" />
                                                        </div>
                                                        <span className="font-medium mr-1">
                                                            Date:
                                                        </span>
                                                        {new Date(
                                                            exam.examCompletedDateUTC,
                                                        ).toLocaleDateString()}
                                                    </div>
                                                )}
                                                {exam.typeOfPowerUnit && (
                                                    <div className="flex items-center">
                                                        <div className="bg-base-200 p-1 rounded-full mr-1.5">
                                                            <FaTruck className="text-primary w-3 h-3" />
                                                        </div>
                                                        <span className="font-medium mr-1">
                                                            Power Unit:
                                                        </span>
                                                        {exam.typeOfPowerUnit}
                                                    </div>
                                                )}
                                                {exam.typeOfTrailer && (
                                                    <div className="flex items-center">
                                                        <div className="bg-base-200 p-1 rounded-full mr-1.5">
                                                            <PiTruckTrailerDuotone className="text-primary w-3 h-3" />
                                                        </div>
                                                        <span className="font-medium mr-1">
                                                            Trailer:
                                                        </span>
                                                        {exam.typeOfTrailer}
                                                    </div>
                                                )}
                                                {exam.milesDriven !== null && (
                                                    <div className="flex items-center">
                                                        <div className="bg-base-200 p-1 rounded-full mr-1.5">
                                                            <FaRoad className="text-primary w-3 h-3" />
                                                        </div>
                                                        <span className="font-medium mr-1">
                                                            Miles:
                                                        </span>
                                                        {exam.milesDriven}
                                                    </div>
                                                )}
                                                {exam.certificateName && (
                                                    <div className="flex items-center">
                                                        <div className="bg-base-200 p-1 rounded-full mr-1.5">
                                                            <FaCertificate className="text-primary w-3 h-3" />
                                                        </div>
                                                        <span className="font-medium mr-1">
                                                            Certificate:
                                                        </span>
                                                        {exam.certificateName}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="sticky bottom-0 bg-base-100 border-t px-4 py-2 flex justify-end">
                        <button
                            onClick={handleCloseModal}
                            className="btn btn-primary btn-sm"
                        >
                            Close
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            </dialog>
        </>
    );
}
